import { useEffect, useState, useMemo } from 'react';
import Card from '../components/card/Card';
import Button from '../components/button/Button';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Icon from '../components/icon/Icon';
import Tabs from '../components/tabs/Tabs';
import { getApplicationResume } from '../services/ApplicationService';
import { hasData } from '../utils/Utils';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import AvalesTab from '../organisms/Dashboard/AvalesTab';
import {
  getDashboardStatusCardConfig,
  mergeDashboardStatusSections,
  resolveDashboardLayout,
} from '../utils/dashboardStatusCards';

type DashboardSection = {
  code: string;
  pending: number;
};

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowSize(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

const STATUS_ICON_SIZE = 28;

const Dashboard = () => {
  const config = useConfig();
  const windowWidth = useWindowSize();
  const greetingName =
    localStorage.getItem('email') || localStorage.getItem('full_name') || '';
  const [sections, setSections] = useState<DashboardSection[]>([]);
  const navigate = useNavigate();

  const isMobile = windowWidth <= 1500;
  const isGuaranteesEnabled = config.isGuaranteesEnabled === true;
  const dashboardLayout = useMemo(
    () => resolveDashboardLayout(config),
    [config]
  );

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    getApplicationResume().then((response) => {
      if (hasData(response)) setSections(response.data);
    });
  };

  const statusSections = useMemo(
    () => mergeDashboardStatusSections(sections, dashboardLayout.cardOrder),
    [sections, dashboardLayout.cardOrder]
  );

  const solicitudesContent = (
    <Card
      margin={{ top: '4' }}
      style={
        isMobile
          ? {
              display: 'flex',
              flexWrap: 'wrap',
              gap: '16px',
            }
          : {
              display: 'grid',
              gridTemplateColumns: `repeat(${statusSections.length}, minmax(0, 1fr))`,
              gap: '16px',
            }
      }
    >
      {statusSections.map((section) => {
        const cardConfig = getDashboardStatusCardConfig(
          section.code,
          dashboardLayout.mode
        );
        const label = cardConfig?.label ?? section.code;
        const route = cardConfig?.route ?? '/dashboard';
        const iconName = cardConfig?.iconName ?? 'origination_outline';

        return (
          <Card
            key={section.code}
            variant='neutral'
            modifier='regular'
            spacing='4'
            onClick={() => navigate(route)}
            style={{
              cursor: 'pointer',
              minWidth: isMobile ? '220px' : 0,
              flex: isMobile ? '1 1 220px' : undefined,
            }}
          >
            <Card
              display='flex'
              flexDirection='column'
              alignItems='center'
              justifyContent='center'
              gap='12px'
            >
              <Card
                display='flex'
                gap='8px'
                alignItems='center'
                justifyContent='center'
              >
                <Icon
                  name={iconName}
                  variant='primary'
                  height={STATUS_ICON_SIZE}
                  width={STATUS_ICON_SIZE}
                />
                <P
                  type='3'
                  variant='neutral'
                  modifier='weak'
                  weight='bold'
                  withMargin={false}
                >
                  {label}
                </P>
              </Card>

              <H type='0' weight='bold' modifier='strong' withMargin={false}>
                {section.pending}
              </H>

              {dashboardLayout.showPendingSubtitle && (
                <P
                  type='4'
                  variant='neutral'
                  modifier='weak'
                  withMargin={false}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Solicitudes pendientes
                </P>
              )}
            </Card>
          </Card>
        );
      })}
    </Card>
  );

  const tabs = useMemo(() => {
    const solicitudesTab = {
      label: 'Solicitudes',
      content: solicitudesContent,
    };

    if (!isGuaranteesEnabled) {
      return [solicitudesTab];
    }

    return [solicitudesTab, { label: 'Avales', content: <AvalesTab /> }];
  }, [solicitudesContent, isGuaranteesEnabled]);

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing='4'
      height='-webkit-fill-available'
      width='85vw'
    >
      <Card display='flex' justifyContent='space-between' alignItems='center'>
        <Card display='flex' gap='16px' alignItems='center'>
          <H type='1' weight='bold' modifier='strong'>
            Hola, {greetingName}
          </H>
        </Card>
        <Button
          variant='primary'
          size='lg'
          onClick={() => navigate('/clientes')}
        >
          Nueva solicitud
        </Button>
      </Card>

      {isGuaranteesEnabled ? (
        <Tabs tabs={tabs} defaultActiveTab={0} />
      ) : (
        solicitudesContent
      )}
    </Card>
  );
};

export default Dashboard;
